/**
 * Seeded RNG — per /design/specs/5_game_engine_spec.md §1.10 and §12.
 *
 * Uses mulberry32 — deterministic from a 32-bit seed, sufficient quality for
 * game sampling. The same seed always produces the same sequence, which makes
 * hand replays and tests reproducible.
 */

export interface RNG {
  /** Uniform float in [0, 1). */
  next(): number;
  /** Uniform integer in [0, maxExclusive). */
  int(maxExclusive: number): number;
  /** Pick a uniformly random element from a non-empty array. */
  pick<T>(items: readonly T[]): T;
  /** Return a shuffled copy of the input array (Fisher–Yates). */
  shuffle<T>(items: readonly T[]): T[];
}

/**
 * Create a deterministic RNG seeded by `seed` (32-bit). Same seed → same
 * sequence across runs and platforms.
 */
export function mulberry32(seed: number): RNG {
  let state = seed >>> 0;

  function next(): number {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  function int(maxExclusive: number): number {
    if (maxExclusive <= 0) throw new Error('maxExclusive must be > 0');
    return Math.floor(next() * maxExclusive);
  }

  function pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Cannot pick from an empty array');
    return items[int(items.length)]!;
  }

  function shuffle<T>(items: readonly T[]): T[] {
    const result = [...items];
    for (let i = result.length - 1; i > 0; i--) {
      const j = int(i + 1);
      [result[i]!, result[j]!] = [result[j]!, result[i]!];
    }
    return result;
  }

  return { next, int, pick, shuffle };
}

/**
 * Create an RNG seeded by cryptographically-strong entropy. Used at session
 * start; subsequent hand-level RNGs are derived deterministically from the
 * session seed for replayability.
 */
export function cryptoSeededRng(): RNG {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return mulberry32(buf[0]!);
}
